import React, { useState } from 'react';
import SelectField from '../../components/common/SelectField';
import type {
  IntegrationSite,
  IntegrationProject,
  JiraWhiteboardIntegration,
  CreateRecord,
} from '../../shared-types';
import Modal, { ModalHeader } from '../../utils/Modal';
import styles from './create-records.module.css';
import { Button, Field, FormRow, SummaryField } from './form';
import useIntegrationRecordsModalController, {
  type IntegrationRecordsModalController,
} from './IntegrationRecordsModalController';

interface CreateIntegrationRecordsModalProps {
  integration: JiraWhiteboardIntegration;
  data: CreateRecord[];
  cancel: () => void;
}

type Props = CreateIntegrationRecordsModalProps &
  Parameters<typeof useIntegrationRecordsModalController>[0];

const IntegrationRecordsModal: React.FC<Props> = ({
  cancel,
  integration,
  data,
  integrationsController,
}) => {
  const controller = useIntegrationRecordsModalController({
    integrationsController,
    integration,
    data,
  });

  const title = integration.getRecordTitle(data.length > 1);

  const [selectedTab, setSelectedTab] = useState<'summary' | 'details'>(
    'summary'
  );
  const [loading, setLoading] = useState(false);

  const allowSubmit =
    controller.recordsValid() && controller.fieldsValid() && !loading;

  return (
    <Modal onClose={cancel}>
      <div className={styles.modalWrapper}>
        <ModalHeader onClose={cancel} modalTitle={`Create ${title}`} />

        <div className={styles.modalBody}>
          <div className={styles.tabHeader}>
            <aha-tab
              selected={selectedTab === 'summary'}
              onClick={() => setSelectedTab('summary')}
            >
              Summary
            </aha-tab>
            <aha-tab
              selected={selectedTab === 'details'}
              onClick={() => setSelectedTab('details')}
            >
              Details
            </aha-tab>

            <div className={styles.issueCount}>
              {data.length} new{' '}
              {integration.getRecordTitle(data.length !== 1, false)}
            </div>
          </div>

          {selectedTab === 'summary' && (
            <div className={styles.formWrapper}>
              {data.map((shape, index) => (
                <FormRow
                  key={shape.id}
                  label={index === 0 ? 'Summary' : undefined}
                  required
                >
                  <SummaryField
                    controller={controller}
                    shapeId={shape.id}
                    key={shape.id}
                  />
                </FormRow>
              ))}
            </div>
          )}

          {selectedTab === 'details' && (
            <>
              {data.length > 1 && (
                <aha-alert class={styles.warningMessage}>
                  Details will apply to all selected shapes
                </aha-alert>
              )}
              <Details integration={integration} controller={controller} />
            </>
          )}
        </div>

        <aha-modal-footer>
          {selectedTab === 'summary' ? (
            <>
              <Button onClick={cancel}>Cancel</Button>

              <Button
                primary
                disabled={!controller.recordsValid()}
                onClick={() => setSelectedTab('details')}
              >
                Next
                <i className='fa-regular fa-arrow-right' />
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setSelectedTab('summary')}>Back</Button>

              <Button
                primary
                type='submit'
                disabled={!allowSubmit}
                onClick={() => {
                  if (allowSubmit) {
                    setLoading(true);
                    void controller.actionCreateRecords().then(() => {
                      setLoading(false);
                    });
                  }
                }}
              >
                Create {title}
              </Button>
            </>
          )}
        </aha-modal-footer>
      </div>
    </Modal>
  );
};

interface DetailsProps {
  integration: JiraWhiteboardIntegration;
  controller: IntegrationRecordsModalController;
}

const Details: React.FC<DetailsProps> = ({ integration, controller }) => {
  const { selectedProject, selectedIssueTypeId } = controller.state;
  const currentFields = controller.getCurrentFields();
  const { sites } = controller;
  const selectedSite = controller.getSelectedSite();

  if (!sites) {
    return (
      <div className={styles.loader}>
        <aha-loading-dots />
      </div>
    );
  }

  return (
    <div className={styles.formWrapper}>
      {/* only show a site selector if there is more than one */}
      {sites.length > 1 && (
        <FormRow label='Account'>
          <SelectField<IntegrationSite>
            setValue={value => {
              controller.actionSetSelectedSite(value.item);
            }}
            initialValue={selectedSite?.id}
            getOptions={async () => {
              const result = await integration.getSites({
                options: { authenticate: true },
              });

              if (result.status === 'error') {
                console.warn('Error loading sites', result.error);
                return { options: [] };
              }

              return {
                options: result.data.sites.map(site => ({
                  value: site.id,
                  text: site.name,
                  item: site,
                })),
              };
            }}
          />
        </FormRow>
      )}
      <FormRow label='Project'>
        {selectedSite && (
          <SelectField<IntegrationProject>
            key={selectedSite.id}
            setValue={value => {
              controller.actionSetSelectedProject(value.item);
            }}
            initialValue={selectedProject?.id}
            getOptions={async ({ page, searchParam }) => {
              const result = await integration.getProjects({
                siteId: selectedSite.id,
                searchParam,
                page,
                options: { authenticate: true },
              });

              if (result.status === 'error') {
                console.warn('Error loading projects', result.error);
                return { options: [] };
              }

              return {
                options: result.data.projects.map(project => ({
                  value: project.id,
                  text: project.name,
                  item: project,
                })),
                pages: result.data.pages,
              };
            }}
          />
        )}
      </FormRow>
      {selectedSite && selectedProject && (
        <FormRow label='Issue type'>
          <SelectField
            key={selectedProject.id}
            setValue={value =>
              controller.actionSetSelectedIssueTypeId(value.item)
            }
            initialValue={selectedIssueTypeId}
            getOptions={async () => ({
              options: selectedProject.recordTypes.map(issue => ({
                value: issue.id,
                text: issue.name,
                item: issue.id,
              })),
            })}
          />
        </FormRow>
      )}
      {selectedIssueTypeId &&
        currentFields?.map(field => (
          <FormRow
            key={field.name}
            required={field.required}
            label={field.description}
          >
            <Field
              key={`${selectedIssueTypeId}_${field.name}`}
              controller={controller}
              field={field}
            />
          </FormRow>
        ))}
    </div>
  );
};

export default IntegrationRecordsModal;
